import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api/client';
import socketService from '../api/socket';

// Normalize content for robust matching: trim + collapse whitespace
const norm = (s) => (s ?? '').trim().replace(/\s+/g, ' ');

// Flatten nested content: backend may return { content: { content: "text" } }
const flattenContent = (msg) => {
  if (!msg) return msg;
  const c = msg.content;
  if (c !== null && typeof c === 'object') {
    return {
      ...msg,
      content: c.content ?? c.text ?? c.body ?? c.message ?? JSON.stringify(c),
    };
  }
  return msg;
};

// Stable key for a message: prefer _id, then id, then undefined
const msgKey = (m) => {
  const k = m._id || m.id;
  return k ? String(k) : null;
};

// Extract the messages array from whatever shape the backend returns.
// Handles: [..] | { data:[..] } | { data:{ messages:[..] } } | { messages:[..] }
//          | { data:{ docs:[..] } } | { results:[..] } …
const MSG_KEYS = ['messages', 'data', 'docs', 'results', 'items'];
const pickArray = (payload, keys) => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== 'object') return null;
  for (const k of keys) if (Array.isArray(payload[k])) return payload[k];
  for (const k of keys) {
    const found = payload[k] && pickArray(payload[k], keys);
    if (found) return found;
  }
  return null;
};

export const useWhatsAppMessages = (conversationId) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const mountedRef = useRef(true);
  // Ref-based sending guard → immune to stale closure on rapid double-click
  const sendingRef = useRef(false);
  // IDs of messages sent by the human operator from this session
  const operatorMsgIds = useRef(new Set());

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      if (mountedRef.current) {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
    }, 50);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    operatorMsgIds.current = new Set();

    if (!conversationId) {
      setMessages([]);
      return;
    }

    // ── Fetch messages ────────────────────────────────────────────────────
    setLoading(true);
    setError(null);
    setMessages([]);

    api
      .get(`/whatsflow/conversations/${conversationId}/messages`)
      .then((res) => {
        if (!mountedRef.current) return;
        const raw = pickArray(res.data, MSG_KEYS) || [];
        if (raw.length === 0 && res.data && Object.keys(res.data).length) {
          // The request succeeded but no array was found → response shape is
          // unexpected. Log it so we can adapt MSG_KEYS to the real payload.
          console.warn('[WhatsApp] Réponse messages sans tableau exploitable:', res.data);
        }
        setMessages(raw.map(flattenContent));
        scrollToBottom();
      })
      .catch((err) => {
        if (!mountedRef.current) return;
        console.error('Error loading messages:', err);
        setError('Impossible de charger les messages');
      })
      .finally(() => {
        if (mountedRef.current) setLoading(false);
      });

    // ── Socket setup ──────────────────────────────────────────────────────
    if (!socketService.socket) {
      const token = localStorage.getItem('accessToken');
      socketService.connect(token);
    }
    const socket = socketService.socket;
    if (!socket) return;

    const room = `conversation:${conversationId}`;
    const joinRoom = () => socket.emit('join:room', { room });
    joinRoom();
    socket.on('connect', joinRoom);

    const handleNewMessage = (data) => {
      if (!mountedRef.current) return;
      const msg = flattenContent(data.message || data);

      // Guard: only process messages for this conversation
      // Use String() to normalise ObjectId vs string
      if (
        msg.conversationId == null ||
        String(msg.conversationId) !== String(conversationId)
      ) return;

      const realKey = msgKey(msg);

      setMessages((prev) => {
        // ── 1. Already present by real ID → deduplicate ──────────────────
        if (realKey && prev.some((m) => !m._isOptimistic && msgKey(m) === realKey)) {
          // If it's there as 'agent' but we now know it's 'operator', fix the type
          if (operatorMsgIds.current.has(realKey)) {
            return prev.map((m) =>
              msgKey(m) === realKey ? { ...m, senderType: 'operator' } : m
            );
          }
          return prev;
        }

        // ── 2. Matches an optimistic placeholder? (content matching) ─────
        //    Handles the race where socket arrives before API response.
        const msgNorm = norm(msg.content);
        const optimisticIdx = prev.findIndex(
          (m) => m._isOptimistic && norm(m.content) === msgNorm
        );
        if (optimisticIdx >= 0) {
          // Promote: real message replaces optimistic, keep operator type
          if (realKey) operatorMsgIds.current.add(realKey);
          return prev.map((m, i) =>
            i === optimisticIdx
              ? { ...msg, senderType: 'operator', _isOptimistic: false }
              : m
          );
        }

        // ── 3. Known operator ID (API responded before socket) ───────────
        if (realKey && operatorMsgIds.current.has(realKey)) {
          return prev.map((m) =>
            msgKey(m) === realKey ? { ...m, senderType: 'operator' } : m
          );
        }

        // ── 4. New incoming message from client or AI ────────────────────
        return [...prev, { ...msg }];
      });

      scrollToBottom();
    };

    socket.on('message:new', handleNewMessage);

    return () => {
      mountedRef.current = false;
      socket.off('message:new', handleNewMessage);
      socket.off('connect', joinRoom);
      if (socket.connected) socket.emit('leave:room', { room });
    };
  }, [conversationId, scrollToBottom]);

  const sendReply = useCallback(
    async (content) => {
      // Use ref-based guard — immune to stale closure on rapid double-click
      if (!conversationId || !content?.trim() || sendingRef.current) return false;

      const trimmedContent = content.trim();
      const tempId = `opt-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

      // ── STEP 1: Optimistic update ─────────────────────────────────────
      // Message appears immediately as 'operator' (blue) before any network round-trip.
      const optimisticMsg = {
        _id: tempId,
        _isOptimistic: true,
        conversationId,
        senderType: 'operator',
        content: trimmedContent,
        contentType: 'text',
        isRead: false,
        createdAt: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, optimisticMsg]);
      scrollToBottom();

      sendingRef.current = true;
      setSending(true);

      try {
        // ── STEP 2: API call ──────────────────────────────────────────────
        const res = await api.post(
          `/whatsflow/conversations/${conversationId}/reply`,
          {
            content: trimmedContent,
            contentType: 'text',
            senderType: 'operator',
            isManualReply: true,
          }
        );

        // Strict success check
        const ok =
          (res.status === 200 || res.status === 201) &&
          res.data?.success !== false;

        if (!ok) {
          // Remove optimistic on clear failure
          setMessages((prev) => prev.filter((m) => m._id !== tempId));
          return false;
        }

        const serverMsg = flattenContent(res.data?.data);

        if (serverMsg) {
          const realKey = msgKey(serverMsg);

          if (realKey) {
            operatorMsgIds.current.add(realKey);
            // Limit Set size to prevent unbounded growth
            if (operatorMsgIds.current.size > 500) {
              const first = operatorMsgIds.current.values().next().value;
              operatorMsgIds.current.delete(first);
            }
          }

          setMessages((prev) => {
            // If socket already promoted our optimistic, remove the stale optimistic
            // and ensure the real one is marked as operator.
            const hasReal = realKey && prev.some((m) => !m._isOptimistic && msgKey(m) === realKey);
            if (hasReal) {
              return prev
                .filter((m) => m._id !== tempId)
                .map((m) =>
                  msgKey(m) === realKey ? { ...m, senderType: 'operator' } : m
                );
            }

            // Normal path: replace optimistic with confirmed server message
            return prev.map((m) =>
              m._id === tempId
                ? { ...serverMsg, senderType: 'operator', _isOptimistic: false }
                : m
            );
          });
        }
        // If no serverMsg body: optimistic stays. Socket event will arrive and
        // content-match it (handleNewMessage step 2), replacing it cleanly.

        return true;
      } catch (err) {
        console.error('Error sending reply:', err);
        setMessages((prev) => prev.filter((m) => m._id !== tempId));
        return false;
      } finally {
        sendingRef.current = false;
        if (mountedRef.current) setSending(false);
      }
    },
    [conversationId, scrollToBottom] // no 'sending' dep — we use sendingRef
  );

  return { messages, loading, error, sendReply, sending, messagesEndRef };
};

export default useWhatsAppMessages;
